import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, PhoneCall, Star, Flame, MapPin, MessageCircle, FileText } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import AssessmentForm from "../components/AssessmentForm";
import { BLOG_POSTS } from "../data/blog";
import { IMAGES, SERVICE_AREAS } from "../data/content";
import { ACTS, SHOWCASE_URL, SPORT_PARAMS, sportKeyForSlug, waLink } from "../data/showcase";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/contact";
import { responsive } from "../lib/img";

const DOMAIN = "https://www.speedyresurfacing.com.au";
const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

const Act = ({ post, i, stat, pinned }) => {
  const act = ACTS[post.slug] || {};
  const flip = i % 2 === 1;
  const hot = i === 0 && (stat?.score || 0) > 0;
  const href = `/blog-articles/${post.slug}`;
  return (
    <Reveal>
      <article id={sportKeyForSlug(post.slug)} data-testid={`showcase-act-${post.slug}`}
               className={`grid items-center gap-6 md:grid-cols-12 md:gap-10 ${i === 0 ? "rounded-lg border border-gold/30 bg-obsidian-800/70 p-6 md:p-10" : "border-t border-white/10 pt-10"}`}>
        <div className={`md:col-span-2 ${flip ? "md:order-3 md:text-right" : ""}`}>
          <span className="act-number font-heading font-black text-6xl leading-none md:text-8xl">{String(i + 1).padStart(2, "0")}</span>
        </div>
        <div className={`md:col-span-6 ${flip ? "md:order-2" : ""}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{act.act || post.category}</span>
            {i === 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-gold px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-obsidian" data-testid="showcase-headliner-badge">
                <Flame size={12} /> {pinned ? "Featured for your search" : hot ? "Most read this month" : "Now taking the stage"}
              </span>
            )}
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl leading-[0.95] tracking-tighter md:text-5xl" data-testid={`showcase-act-headline-${post.slug}`}>
            {act.headline || post.title}
          </h2>
          <p className="mt-4 max-w-xl text-base text-zinc-300 md:text-lg">{act.pitch || post.dek}</p>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            {(act.points || post.keyTakeaways.slice(0, 4)).map((pt) => (
              <li key={pt} className="flex gap-2 text-sm text-zinc-200"><Star size={14} className="mt-0.5 shrink-0 text-gold" />{pt}</li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            {(act.geo || []).map((g) => (
              <span key={g} className="inline-flex items-center gap-1 rounded-full border border-white/12 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-400"><MapPin size={11} />{g}</span>
            ))}
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/quote" state={{ sport: act.sport || post.category, source: post.slug }} data-testid={`showcase-act-quote-${post.slug}`}
                  className="inline-flex h-12 items-center gap-2 rounded-sm bg-gold px-6 font-heading font-black text-obsidian shadow-gold transition-colors hover:bg-gold-hover">
              <FileText size={16} /> Get a Quote
            </Link>
            <a href={waLink(act.whatsapp || `Hi Speedy Resurfacing, I'd like a quote for ${(act.sport || post.category).toLowerCase()} court resurfacing in Sydney.`)}
               target="_blank" rel="noopener noreferrer" data-testid={`showcase-act-whatsapp-${post.slug}`}
               className="inline-flex h-12 items-center gap-2 rounded-sm border border-neon/50 px-6 font-bold text-neon transition-colors hover:bg-neon/10 hover:border-neon">
              <MessageCircle size={16} /> WhatsApp us
            </a>
            <Link to={href} data-testid={`showcase-act-link-${post.slug}`}
                  className="group inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 transition-colors hover:text-gold">
              Read the full guide <ArrowUpRight size={13} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            {stat?.views > 0 && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid={`showcase-act-stats-${post.slug}`}>
                {fmt(stat.views)} {stat.views === 1 ? "read" : "reads"}{stat.enquiries ? ` · ${stat.enquiries} ${stat.enquiries === 1 ? "enquiry" : "enquiries"}` : ""}
              </span>
            )}
          </div>
        </div>
        <Link to={href} className={`relative block aspect-[4/3] overflow-hidden rounded-lg border border-white/10 md:col-span-4 ${flip ? "md:order-1" : ""}`}>
          <img {...responsive(IMAGES[post.hero], "(min-width: 768px) 420px, 100vw", [480, 800])} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
          <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{act.sport || post.category}</span>
        </Link>
      </article>
    </Reveal>
  );
};

export default function Showcase() {
  const [params] = useSearchParams();
  const variant = SPORT_PARAMS[(params.get("sport") || "").toLowerCase()] || null;
  const pinnedAct = variant ? ACTS[variant.slug] : null;
  const [stats, setStats] = useState({});
  useEffect(() => {
    api.get("/blog/top", { params: { days: 30 } })
      .then((r) => setStats(Object.fromEntries(r.data.map((s) => [s.slug, s]))))
      .catch(() => {});
  }, []);
  const lineup = useMemo(() => {
    const sorted = [...BLOG_POSTS].sort((a, b) => (stats[b.slug]?.score || 0) - (stats[a.slug]?.score || 0));
    if (!variant) return sorted;
    return [...sorted.filter((p) => p.slug === variant.slug), ...sorted.filter((p) => p.slug !== variant.slug)];
  }, [stats, variant]);
  const areas = SERVICE_AREAS.map((a) => (typeof a === "string" ? a : a.name));
  const seoTitle = variant
    ? `${variant.seo} | Speedy Resurfacing`
    : "Court Resurfacing Sydney | Basketball, Netball, Tennis & School Courts — Speedy Resurfacing";

  return (
    <div className="noise" data-testid="showcase-page" data-sport={variant ? params.get("sport").toLowerCase() : undefined}>
      <Seo
        title={seoTitle}
        description="Court resurfacing Sydney: non-slip acrylic systems, crack repair and precision line marking for basketball, netball, tennis, school and accessible SDA courts across Parramatta, Penrith, Liverpool, the North Shore and Greater Sydney. Free site assessment."
        path={SHOWCASE_URL}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Court Resurfacing Sydney — Basketball, Netball, Tennis & School Courts | Speedy Resurfacing",
          url: DOMAIN + SHOWCASE_URL,
          description: "Court resurfacing guides for basketball, netball, tennis, school and accessible SDA courts across Greater Sydney and NSW.",
          publisher: { "@type": "Organization", name: "Speedy Resurfacing", url: DOMAIN },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: lineup.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.title, url: `${DOMAIN}/blog-articles/${p.slug}` })),
          },
        }}
      />

      <section className="relative overflow-hidden pb-20 pt-28 md:pb-28 md:pt-36" data-testid="showcase-hero">
        <div className="stage-spot stage-spot-a" aria-hidden="true" />
        <div className="stage-spot stage-spot-b" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <div className="marquee-bulbs" aria-hidden="true">
            {Array.from({ length: 28 }).map((_, i) => <span key={i} style={{ animationDelay: `${i * 0.08}s` }} />)}
          </div>
          <p className="stage-in mt-8 font-mono text-[11px] uppercase tracking-[0.45em] text-gold" style={{ animationDelay: "0.1s" }}>
            Sydney's court resurfacing specialists · Merrylands HQ · Greater Sydney &amp; NSW
          </p>
          <h1 className="mt-6 font-heading font-black text-4xl leading-[0.9] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="showcase-title">
            <span className="stage-in block" style={{ animationDelay: "0.3s" }}>{variant ? variant.h1 : "COURT RESURFACING"}</span>
            <span className="stage-in gold-text block" style={{ animationDelay: "0.5s" }}>SYDNEY.</span>
            <span className="stage-in block text-2xl sm:text-3xl lg:text-4xl mt-3 text-zinc-100" style={{ animationDelay: "0.7s" }}>
              {variant ? variant.sub : <>BASKETBALL · NETBALL · TENNIS · PICKLEBALL · SCHOOL &amp; ACCESSIBLE COURTS</>}
            </span>
          </h1>
          <p className="stage-in mt-8 max-w-2xl text-base text-zinc-300 md:text-lg" style={{ animationDelay: "1.1s" }}>
            {pinnedAct ? pinnedAct.pitch : (
              <>Non-slip acrylic resurfacing, crack repair and precision line marking for courts across Parramatta, Penrith,
              Liverpool, the North Shore, the Sutherland Shire and Greater Sydney — every guide below ranked by what Sydney
              court owners are reading right now.</>
            )}
          </p>
          <div className="stage-in mt-10 flex flex-wrap gap-3" style={{ animationDelay: "1.3s" }}>
            <a href={PHONE_TEL} data-testid="showcase-call"
               className="inline-flex items-center gap-2 rounded-sm bg-gold px-7 py-4 font-heading font-black text-obsidian shadow-gold transition-colors hover:bg-gold-hover">
              <PhoneCall size={18} /> Call {PHONE_DISPLAY}
            </a>
            <a href={waLink(pinnedAct?.whatsapp || "Hi Speedy Resurfacing, I'd like a quote for court resurfacing in Sydney.")} target="_blank" rel="noopener noreferrer" data-testid="showcase-whatsapp"
               className="inline-flex items-center gap-2 rounded-sm border border-neon/50 px-7 py-4 font-bold text-neon transition-colors hover:border-neon hover:bg-neon/10">
              <MessageCircle size={18} /> WhatsApp us
            </a>
            <a href="#enquire" data-testid="showcase-cta-form"
               className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-7 py-4 font-bold text-white transition-colors hover:border-gold hover:text-gold">
              Request a Site Assessment <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-5 pb-24 md:px-10" data-testid="showcase-lineup">
        <Overline className="text-gold">The lineup · ranked by reads &amp; enquiries, last 30 days</Overline>
        <div className="mt-10 space-y-10 md:space-y-14">
          {lineup.map((p, i) => <Act key={p.slug} post={p} i={i} stat={stats[p.slug]} pinned={!!variant && p.slug === variant.slug} />)}
        </div>
      </section>

      <section className="border-y border-gold/30 bg-obsidian-800 py-14" data-testid="showcase-geo">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Overline className="text-gold">Encore · on tour across Sydney &amp; NSW</Overline>
          <h2 className="mt-4 font-heading font-black text-2xl tracking-tight md:text-4xl">ONE CREW. EVERY POSTCODE FROM MERRYLANDS TO THE COAST.</h2>
          <p className="mt-3 max-w-2xl text-zinc-300">
            Court resurfacing, custom court design and accessible line marking — based at 545 Merrylands Road, Merrylands, and
            booked across Greater Sydney, Wollongong, Newcastle and regional NSW.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {areas.map((a) => (
              <span key={a} className="inline-flex items-center gap-1 rounded-full border border-gold/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-300"><MapPin size={11} className="text-gold" />{a}</span>
            ))}
          </div>
        </div>
      </section>

      <section id="enquire" className="mx-auto max-w-7xl px-5 py-20 md:px-10" data-testid="showcase-enquire">
        <Overline className="text-gold">Grand finale</Overline>
        <h2 className="mt-4 mb-8 font-heading font-black text-3xl tracking-tight md:text-5xl">THE ONLY ENCORE WE NEED: <span className="gold-text">YOUR COURT.</span></h2>
        <AssessmentForm testid="showcase-form" source="showcase" />
      </section>
    </div>
  );
}
